import { t } from 'i18next';
import { useMutation } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors } from 'core/hooks';
import { discountService } from 'corporate/services';
import { IUpsertPricingProfileRulePayload } from 'pricing/types';

interface IProps {
  selectionCount?: number;
  position?: NotificationPosition;
}

export const useRuleActions = ({ selectionCount, position }: IProps = {}) => {
  return {
    ...useUpsertRule(),
    ...useRemoveRules(selectionCount, position),
  };
};

const useUpsertRule = () => {
  const { errors } = useHandleErrors({
    success: (payload: IUpsertPricingProfileRulePayload) => ({
      message: payload?.id
        ? t('messagePricingProfileRuleUpdateSuccess')
        : t('messagePricingProfileRuleCreateSuccess'),
    }),
    loading: (payload: IUpsertPricingProfileRulePayload) => ({
      message: payload?.id
        ? t('messagePricingProfileRuleUpdateInProgress')
        : t('messagePricingProfileRuleCreateInProgress'),
    }),
    error: (payload: IUpsertPricingProfileRulePayload) => ({
      title: payload?.id
        ? t('errorTitlePricingProfileRuleUpdate')
        : t('errorTitlePricingProfileRuleCreate'),
    }),
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(
    (payload: IUpsertPricingProfileRulePayload) => discountService.upsertDiscount(payload),
    {
      ...errors,
    }
  );

  return {
    upsertRule: mutateAsync,
  };
};

const useRemoveRules = (
  selectionCount: number,
  position: NotificationPosition = NotificationPosition.TOP
) => {
  const { errors } = useHandleErrors({
    success: { message: t('messagePricingProfileRulesRemoveSuccess', { count: selectionCount }) },
    loading: {
      message: t('messagePricingProfileRulesRemoveInProgress', { count: selectionCount }),
    },
    error: { title: t('errorTitlePricingProfileRulesRemove', { count: selectionCount }) },
    position,
  });

  const { mutateAsync } = useMutation(
    (data: { ids: number[] }) => discountService.removeDiscounts(data),
    {
      ...errors,
    }
  );

  const removeRules = (data: { ids: number[] }) => mutateAsync(data);

  return {
    removeRules,
  };
};
